import React, { useState, useEffect, useCallback } from 'react';
import {
    ClipboardList, Plus, Trash2, Play, CheckCircle, XCircle, Clock, Loader,
    AlertCircle, RefreshCw, ArrowRight, Lock, Edit2
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { moveProcessService } from '../../../../services/moveProcessService';
import {
    MoveProcess, MoveProcessStatus, MoveProcessStepStatus,
    MOVE_PROCESS_STATUS_LABELS, MOVE_PROCESS_STEP_STATUS_LABELS,
    ValidateMoveProcessResponse, ExecuteMoveProcessResponse
} from './moveProcessTypes';
import { NewMoveProcessModal } from './NewMoveProcessModal';
import { AddStepModal } from './AddStepModal';

interface MoveProcessesAppProps {
    onBack?: () => void;
}

export const MoveProcessesApp: React.FC<MoveProcessesAppProps> = ({ onBack }) => {
    const { user } = useAuth();
    const { currentWarehouse } = useWarehouse();
    const [processes, setProcesses] = useState<MoveProcess[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [selected, setSelected] = useState<MoveProcess | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showNewModal, setShowNewModal] = useState(false);
    const [showAddStep, setShowAddStep] = useState(false);
    const [validation, setValidation] = useState<ValidateMoveProcessResponse | null>(null);
    const [executeResult, setExecuteResult] = useState<ExecuteMoveProcessResponse | null>(null);
    const [isBusy, setIsBusy] = useState(false);

    const warehouseID = currentWarehouse || '1';
    const companyNo = user?.companyNo || '';

    const loadProcesses = useCallback(async () => {
        if (!companyNo) return;
        setLoading(true);
        setError(null);
        try {
            const data = await moveProcessService.list(warehouseID);
            setProcesses(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunne ikke hente planer');
        } finally {
            setLoading(false);
        }
    }, [companyNo, warehouseID]);

    const loadSelected = useCallback(async (id: number) => {
        try {
            const data = await moveProcessService.getById(id);
            setSelected(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunne ikke hente plan');
        }
    }, []);

    useEffect(() => { loadProcesses(); }, [loadProcesses]);
    useEffect(() => {
        if (selectedId) loadSelected(selectedId);
        else setSelected(null);
    }, [selectedId, loadSelected]);

    const handleCreate = async (name: string, description: string, scheduledFor?: string) => {
        setIsBusy(true);
        try {
            const created = await moveProcessService.create({
                warehouseID, name, description: description || undefined,
                scheduledFor: scheduledFor || undefined
            });
            setShowNewModal(false);
            await loadProcesses();
            setSelectedId(created.id);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunne ikke opprette plan');
        } finally {
            setIsBusy(false);
        }
    };

    const handleDeleteStep = async (stepId: number) => {
        if (!selected) return;
        if (!window.confirm('Slett dette steget?')) return;
        setIsBusy(true);
        try {
            await moveProcessService.deleteStep(selected.id, stepId);
            await loadSelected(selected.id);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunne ikke slette steg');
        } finally {
            setIsBusy(false);
        }
    };

    const handleDeletePlan = async () => {
        if (!selected) return;
        if (!window.confirm(`Slett planen "${selected.name}"? Dette frigjør alle destinasjons-reservasjoner.`)) return;
        setIsBusy(true);
        try {
            await moveProcessService.delete(selected.id);
            setSelectedId(null);
            await loadProcesses();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunne ikke slette plan');
        } finally {
            setIsBusy(false);
        }
    };

    const handleValidate = async () => {
        if (!selected) return;
        setIsBusy(true);
        try {
            const result = await moveProcessService.validate(selected.id);
            setValidation(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Validering feilet');
        } finally {
            setIsBusy(false);
        }
    };

    const handlePlan = async () => {
        if (!selected) return;
        setIsBusy(true);
        try {
            const result = await moveProcessService.plan(selected.id);
            setValidation(result);
            if (result.canExecute) {
                await loadSelected(selected.id);
                await loadProcesses();
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Planlegging feilet');
        } finally {
            setIsBusy(false);
        }
    };

    const handleExecute = async () => {
        if (!selected) return;
        if (!window.confirm(`Utfør planen "${selected.name}"? Dette oppretter flytteordrer for alle ${selected.stepCount} steg.`)) return;
        setIsBusy(true);
        setExecuteResult(null);
        try {
            const result = await moveProcessService.execute(selected.id);
            setExecuteResult(result);
            await loadSelected(selected.id);
            await loadProcesses();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Utførelse feilet');
        } finally {
            setIsBusy(false);
        }
    };

    const getStatusBadgeClass = (status: number) => {
        switch (status) {
            case MoveProcessStatus.Draft: return 'bg-gray-100 text-gray-700';
            case MoveProcessStatus.Planned: return 'bg-blue-100 text-blue-700';
            case MoveProcessStatus.Executing: return 'bg-amber-100 text-amber-700';
            case MoveProcessStatus.Completed: return 'bg-green-100 text-green-700';
            case MoveProcessStatus.Cancelled: return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const getStepStatusIcon = (status: number) => {
        switch (status) {
            case MoveProcessStepStatus.Pending: return <Clock className="w-4 h-4 text-gray-400" />;
            case MoveProcessStepStatus.Reserved: return <Lock className="w-4 h-4 text-blue-500" />;
            case MoveProcessStepStatus.Executed: return <CheckCircle className="w-4 h-4 text-green-500" />;
            case MoveProcessStepStatus.Failed: return <XCircle className="w-4 h-4 text-red-500" />;
            case MoveProcessStepStatus.Skipped: return <XCircle className="w-4 h-4 text-gray-400" />;
            default: return null;
        }
    };

    const canAddSteps = selected?.status === MoveProcessStatus.Draft;
    const canPlan = selected?.status === MoveProcessStatus.Draft && (selected?.stepCount ?? 0) > 0;
    const canExecute = selected
        && (selected.status === MoveProcessStatus.Planned || selected.status === MoveProcessStatus.Executing)
        && selected.steps.some(s => s.status !== MoveProcessStepStatus.Executed);

    return (
        <div className="flex flex-col h-screen w-full overflow-hidden bg-gray-50">
            <div className="flex items-center justify-between bg-white px-4 py-2 border-b border-gray-200 shadow-sm flex-shrink-0">
                <div className="flex items-center space-x-3">
                    <BackButton onClick={onBack || (() => {})} />
                    <ClipboardList className="w-5 h-5 text-purple-600" />
                    <div>
                        <h1 className="text-lg font-bold text-gray-800">Flytteplaner</h1>
                        <p className="text-xs text-gray-600">
                            Lager: {warehouseID} | Selskap: {companyNo}
                        </p>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={loadProcesses}
                        disabled={loading}
                        className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        <span>Oppdater</span>
                    </button>
                    <button
                        onClick={() => setShowNewModal(true)}
                        className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-semibold"
                    >
                        <Plus className="w-4 h-4" />
                        <span>Ny plan</span>
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border-b border-red-200 text-red-800 px-4 py-2 text-sm flex items-start justify-between">
                    <div className="flex items-start space-x-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                    <button onClick={() => setError(null)} className="text-xs underline">Lukk</button>
                </div>
            )}

            <div className="flex-1 flex min-h-0">
                {/* Left: list */}
                <div className="w-80 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
                    <div className="px-4 py-2 border-b border-gray-100 flex-shrink-0">
                        <h2 className="text-sm font-semibold text-gray-700">Planer ({processes.length})</h2>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {loading && processes.length === 0 ? (
                            <div className="p-6 flex items-center justify-center">
                                <Loader className="w-5 h-5 animate-spin text-purple-600" />
                            </div>
                        ) : processes.length === 0 ? (
                            <div className="p-6 text-center text-sm text-gray-500">
                                Ingen planer ennå. Klikk "Ny plan" for å begynne.
                            </div>
                        ) : (
                            processes.map(p => (
                                <button
                                    key={p.id}
                                    onClick={() => setSelectedId(p.id)}
                                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${
                                        selectedId === p.id ? 'bg-purple-50 border-l-4 border-l-purple-500' : ''
                                    }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-sm truncate">{p.name}</div>
                                            {p.description && (
                                                <div className="text-xs text-gray-500 truncate">{p.description}</div>
                                            )}
                                        </div>
                                        <span className={`ml-2 px-2 py-0.5 rounded text-[10px] font-semibold ${getStatusBadgeClass(p.status)}`}>
                                            {MOVE_PROCESS_STATUS_LABELS[p.status as MoveProcessStatus]}
                                        </span>
                                    </div>
                                    <div className="mt-1 text-xs text-gray-600">
                                        {p.completedStepCount}/{p.stepCount} steg
                                        {p.scheduledFor && ` · Planlagt: ${new Date(p.scheduledFor).toLocaleDateString('nb-NO')}`}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Right: detail */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {!selected ? (
                        <div className="flex-1 flex items-center justify-center text-gray-500">
                            <div className="text-center">
                                <ClipboardList className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                                <p>Velg en plan for å se detaljer</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2">
                                        <h2 className="text-lg font-bold truncate">{selected.name}</h2>
                                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusBadgeClass(selected.status)}`}>
                                            {MOVE_PROCESS_STATUS_LABELS[selected.status as MoveProcessStatus]}
                                        </span>
                                    </div>
                                    {selected.description && (
                                        <p className="text-sm text-gray-600 mt-0.5">{selected.description}</p>
                                    )}
                                </div>
                                <div className="flex items-center space-x-2">
                                    {canAddSteps && (
                                        <button
                                            onClick={() => setShowAddStep(true)}
                                            disabled={isBusy}
                                            className="flex items-center space-x-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm font-semibold disabled:opacity-50"
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                            <span>Legg til steg</span>
                                        </button>
                                    )}
                                    {canAddSteps && (
                                        <button
                                            onClick={handleValidate}
                                            disabled={isBusy}
                                            className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm font-semibold disabled:opacity-50"
                                        >
                                            Valider
                                        </button>
                                    )}
                                    {canPlan && (
                                        <button
                                            onClick={handlePlan}
                                            disabled={isBusy}
                                            className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-semibold disabled:opacity-50"
                                        >
                                            <Lock className="w-3.5 h-3.5" />
                                            <span>Reserver destinasjoner</span>
                                        </button>
                                    )}
                                    {canExecute && (
                                        <button
                                            onClick={handleExecute}
                                            disabled={isBusy}
                                            className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-semibold disabled:opacity-50"
                                        >
                                            <Play className="w-3.5 h-3.5" />
                                            <span>Utfør plan</span>
                                        </button>
                                    )}
                                    <button
                                        onClick={handleDeletePlan}
                                        disabled={isBusy}
                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                        title="Slett plan"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {validation && (
                                <div className="bg-blue-50 border-b border-blue-200 px-6 py-2 text-xs text-blue-900 flex-shrink-0">
                                    {validation.errors.length > 0 && (
                                        <div className="font-semibold text-red-700 mb-1">Feil:</div>
                                    )}
                                    {validation.errors.map((e, i) => <div key={i} className="text-red-700">• {e}</div>)}
                                    {validation.warnings.map((w, i) => <div key={i} className="text-amber-700">• {w}</div>)}
                                    {validation.dependencies.length > 0 && (
                                        <div className="mt-1">
                                            <span className="font-semibold">Avhengigheter: </span>
                                            {validation.dependencies.length} detektert
                                        </div>
                                    )}
                                </div>
                            )}

                            {executeResult && (
                                <div className={`border-b px-6 py-2 text-xs flex-shrink-0 ${
                                    executeResult.failedStepCount === 0
                                        ? 'bg-green-50 border-green-200 text-green-800'
                                        : 'bg-amber-50 border-amber-200 text-amber-800'
                                }`}>
                                    <div className="font-semibold">
                                        {executeResult.failedStepCount === 0
                                            ? `✓ ${executeResult.executedStepCount} steg utført`
                                            : `${executeResult.executedStepCount} lykkes, ${executeResult.failedStepCount} feilet`}
                                    </div>
                                    {executeResult.stcOrderNumbers.length > 0 && (
                                        <div className="mt-0.5">
                                            Opprettet flytteordre: {executeResult.stcOrderNumbers.join(', ')}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="flex-1 overflow-auto p-6">
                                <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                                    <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                                        <tr>
                                            <th className="px-3 py-2 text-left w-12">#</th>
                                            <th className="px-3 py-2 text-left w-10"></th>
                                            <th className="px-3 py-2 text-left">Fra</th>
                                            <th className="px-3 py-2 text-left">Til</th>
                                            <th className="px-3 py-2 text-left">Vare</th>
                                            <th className="px-3 py-2 text-right">Mengde</th>
                                            <th className="px-3 py-2 text-left">Status</th>
                                            <th className="px-3 py-2 text-left">Ordre</th>
                                            <th className="px-3 py-2 w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selected.steps.length === 0 ? (
                                            <tr>
                                                <td colSpan={9} className="px-3 py-6 text-center text-gray-500 text-sm">
                                                    Ingen steg ennå. Klikk "Legg til steg".
                                                </td>
                                            </tr>
                                        ) : (
                                            selected.steps.map(step => (
                                                <tr key={step.id} className="border-t border-gray-100">
                                                    <td className="px-3 py-2 font-semibold">{step.stepOrder}</td>
                                                    <td className="px-3 py-2">{getStepStatusIcon(step.status)}</td>
                                                    <td className="px-3 py-2 font-mono text-xs">{step.fromBinCode}</td>
                                                    <td className="px-3 py-2">
                                                        <div className="flex items-center">
                                                            <ArrowRight className="w-3 h-3 mr-1 text-gray-400" />
                                                            <span className="font-mono text-xs">{step.toBinCode}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 font-mono text-xs">{step.itemNo}</td>
                                                    <td className="px-3 py-2 text-right">{step.quantity}</td>
                                                    <td className="px-3 py-2 text-xs">
                                                        {MOVE_PROCESS_STEP_STATUS_LABELS[step.status as MoveProcessStepStatus]}
                                                        {step.errorMessage && (
                                                            <div className="text-red-600 text-[10px] mt-0.5">{step.errorMessage}</div>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-xs">
                                                        {step.executedStcOrderNo ? `#${step.executedStcOrderNo}` : '—'}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        {canAddSteps && (
                                                            <button
                                                                onClick={() => handleDeleteStep(step.id)}
                                                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                                title="Slett steg"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {showNewModal && (
                <NewMoveProcessModal
                    isBusy={isBusy}
                    onClose={() => setShowNewModal(false)}
                    onCreate={handleCreate}
                />
            )}

            {showAddStep && selected && (
                <AddStepModal
                    processId={selected.id}
                    isBusy={isBusy}
                    onClose={() => setShowAddStep(false)}
                    onAdded={async () => {
                        setShowAddStep(false);
                        await loadSelected(selected.id);
                    }}
                />
            )}
        </div>
    );
};
